import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, Typography, Spin, Alert, App, Progress, Badge, Divider, Tooltip } from 'antd';
import {
  LaptopOutlined,
  FileProtectOutlined,
  ExclamationCircleOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  // GlobalOutlined, // removed unused icon
  BugOutlined,
  // SafetyCertificateOutlined, // removed unused icon
  QuestionCircleOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { activityAPI, deviceAPI, policyAPI, threatIntelligenceAPI, systemAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { APP_VERSION } from '../utils/version';

const { Title, Text } = Typography;

const Dashboard = () => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [stats, setStats] = useState({
    devices: { total: 0, online: 0, offline: 0, pending: 0, by_platform: [] },
    violations: { today: 0, total: 0, unhandled: 0, by_severity: {} },
    policies: { active: 0, total: 0, inactive: 0, by_type: [] },
    security: { riskLevel: 'low', complianceScore: 95, threatCount: 0 },
    deviceGroups: { system: 0, custom: 0, total: 0, systemGroups: [], customGroups: [] }
  });
  const [violationTrend, setViolationTrend] = useState([]);
  const [platformDistribution, setPlatformDistribution] = useState([]);
  const [serverVersion, setServerVersion] = useState(null);
  const [licenseUsage, setLicenseUsage] = useState({ current: 0, max: 0, available: 0, usage_percent: 0 });
  const displayServerVersion = serverVersion || APP_VERSION;
  /* eslint-disable-next-line no-unused-vars */
  const [securityMetrics, setSecurityMetrics] = useState({
    avgResponseTime: 0,
    policyEffectiveness: 0,
    systemHealth: 100
  });

  const loadDashboardData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // 计算今日时间范围（使用本地时区）
      const today = new Date();
      const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      
      // 转换为RFC3339格式的本地时间字符串
      const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const timezoneOffset = -date.getTimezoneOffset();
        const offsetHours = String(Math.floor(Math.abs(timezoneOffset) / 60)).padStart(2, '0');
        const offsetMinutes = String(Math.abs(timezoneOffset) % 60).padStart(2, '0');
        const offsetSign = timezoneOffset >= 0 ? '+' : '-';
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
      };
      
      // 并行加载数据
      const [deviceStatsRes, todayActivityStatsRes, activityTrendRes, policyStatsRes, threatStatsRes, deviceGroupStatsRes, tenantUsageRes] = await Promise.all([
        deviceAPI.getDeviceStats().catch(() => ({ data: { data: { total: 0, online: 0, offline: 0, pending: 0, by_platform: [] } } })),
        // 获取今日活动日志统计
        activityAPI.getActivityLogStats({ start_time: formatLocalDateTime(startOfToday), end_time: formatLocalDateTime(endOfToday) }).catch(() => ({ data: { data: { total: 0, by_activity_type: [], by_status: [], by_threat_level: [] } } })),
        // 获取最近7天的活动日志统计，用于生成真实的活动趋势
        Promise.all(Array.from({ length: 7 }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (6 - i));
          const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
          const startTime = formatLocalDateTime(dayStart);
          const endTime = formatLocalDateTime(dayEnd);
          return activityAPI.getActivityLogStats({ start_time: startTime, end_time: endTime })
            .then(res => ({
              date: date.toISOString().split('T')[0],
              count: res.data.data.total || 0
            }))
            .catch(() => ({
              date: date.toISOString().split('T')[0],
              count: 0
            }));
        })),
        policyAPI.getPolicyStats().catch(() => ({ data: { data: { active: 0, total: 0, inactive: 0, by_type: [] } } })),
        threatIntelligenceAPI.getThreatStats().catch(() => ({ data: { data: { by_threat_level: [] } } })),
        deviceAPI.getDeviceGroupStats().catch(() => ({ data: { data: { system_groups: [], custom_groups: [] } } })),
        deviceAPI.getDeviceUsage().catch(() => ({ data: { data: { current: 0, max: 0, available: 0, usage_percent: 0 } } }))
      ]);

      const deviceData = deviceStatsRes.data.data;
      const todayActivityData = todayActivityStatsRes.data.data;
      const policyData = policyStatsRes.data.data;
      const trendData = activityTrendRes || []; // 使用真实的活动趋势数据
      const threatData = threatStatsRes.data.data;
      const deviceGroupData = deviceGroupStatsRes.data.data;
      const usageData = tenantUsageRes?.data?.data || { current: 0, max: 0, available: 0, usage_percent: 0 };

      // 计算安全指标
      const totalDevices = deviceData.total || 0;
      const onlineDevices = deviceData.online || 0;
      const todayTotalActivities = todayActivityData.total || 0;
      const todayActivityByType = todayActivityData.by_activity_type || [];
      const todayActivityByStatus = todayActivityData.by_status || [];
      
      // 计算今日违规和阻止数据
      const todayBlockedActivities = todayActivityByType.find(item => item.activity_type === 'block')?.count || 0;
      const todayFailedActivities = todayActivityByStatus.find(item => item.status === 'failed')?.count || 0;
      const todayViolations = todayBlockedActivities + todayFailedActivities;
      const todayUnhandledViolations = todayFailedActivities;
      
      // 获取今日威胁等级统计
      const todayThreatByLevel = todayActivityData.by_threat_level || threatData.by_threat_level || [];
      const todayCriticalThreats = todayThreatByLevel.find(item => item.threat_level === 'critical')?.count || 0;
      const todayHighThreats = todayThreatByLevel.find(item => item.threat_level === 'high')?.count || 0;
      const todayMediumThreats = todayThreatByLevel.find(item => item.threat_level === 'medium')?.count || 0;
      const todayLowThreats = todayThreatByLevel.find(item => item.threat_level === 'low')?.count || 0;
      
      // 计算风险等级
      let riskLevel = 'low';
      let complianceScore = 95;
      
      if (todayUnhandledViolations > 10 || todayViolations > 50) {
        riskLevel = 'high';
        complianceScore = Math.max(60, 95 - (todayUnhandledViolations * 2) - (todayViolations * 0.5));
      } else if (todayUnhandledViolations > 5 || todayViolations > 20) {
        riskLevel = 'medium';
        complianceScore = Math.max(75, 95 - (todayUnhandledViolations * 1.5) - (todayViolations * 0.3));
      }

      setStats({
        devices: {
          total: totalDevices,
          online: onlineDevices,
          offline: deviceData.offline || 0,
          pending: deviceData.pending || 0,
          by_platform: deviceData.by_platform || []
        },
        violations: {
          today: todayTotalActivities, // 今日总活动数量
          todayViolations: todayViolations, // 今日违规数量
          total: todayBlockedActivities + todayFailedActivities,
          unhandled: todayUnhandledViolations,
          by_severity: {
            critical: todayCriticalThreats,
            high: todayHighThreats,
            medium: todayMediumThreats,
            low: todayLowThreats
          }
        },
        policies: {
          active: policyData.active || 0,
          total: policyData.total || 0,
          inactive: policyData.inactive || 0,
          by_type: policyData.by_type || []
        },
        security: {
          riskLevel,
          complianceScore: Math.round(complianceScore),
          threatCount: todayUnhandledViolations
        },
        deviceGroups: {
          system: deviceGroupData.system_groups?.length || 0,
          custom: deviceGroupData.custom_groups?.length || 0,
          total: (deviceGroupData.system_groups?.length || 0) + (deviceGroupData.custom_groups?.length || 0),
          systemGroups: deviceGroupData.system_groups || [],
          customGroups: deviceGroupData.custom_groups || []
        }
      });

      // 处理活动趋势数据 - 使用真实的每日活动统计
      setViolationTrend(trendData);
      setPlatformDistribution(deviceData.by_platform || []);
      
      // 计算安全指标
      const onlineRate = totalDevices > 0 ? (onlineDevices / totalDevices) * 100 : 100;
      const policyEffectiveness = policyData.total > 0 ? (policyData.active / policyData.total) * 100 : 100;
      
      setSecurityMetrics({
        avgResponseTime: 150, // 固定响应时间，避免随机数据
        policyEffectiveness: Math.round(policyEffectiveness),
        systemHealth: Math.round(onlineRate)
      });

      setLastUpdate(new Date());
      setLicenseUsage(usageData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      message.error(t('dashboard.message.load_failed', ));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [message, t]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // 获取服务器版本（健康检查）
  useEffect(() => {
    systemAPI.getHealth()
      .then((res) => {
        const v = res?.data?.data?.version;
        if (v) setServerVersion(v);
      })
      .catch(() => {
        // 忽略错误，不影响Dashboard其他数据加载
      });
  }, []);

  // 手动刷新函数
  const handleRefresh = () => {
    loadDashboardData(true);
  };

  const getViolationTrendOption = () => {
    const dates = violationTrend.map(item => item.date);
    const counts = violationTrend.map(item => item.count);

    return {
      title: {
        text: t('dashboard.charts.activity_trend.title', ),
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'normal'
        }
      },
      tooltip: {
        trigger: 'axis',
        formatter: function(params) {
          const data = params[0];
          return `${data.axisValue}<br/>${t('dashboard.charts.activity_trend.tooltip.count_label', )} ${data.value}`;
        }
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLine: {
          lineStyle: {
            color: '#d9d9d9'
          }
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          lineStyle: {
            color: '#d9d9d9'
          }
        }
      },
      series: [
        {
          data: counts,
          type: 'line',
          smooth: true,
          lineStyle: {
            color: '#ff4d4f'
          },
          itemStyle: {
            color: '#ff4d4f'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(255, 77, 79, 0.3)' },
                { offset: 1, color: 'rgba(255, 77, 79, 0.1)' }
              ]
            }
          }
        }
      ],
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      }
    };
  };

  const getPlatformDistributionOption = () => {
    const data = platformDistribution.map(item => ({
      name: item.platform || 'Unknown',
      value: item.count || 0
    }));

    const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'];

    return {
      title: {
        text: t('dashboard.charts.platform_distribution.title', ),
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'normal'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        bottom: 20
      },
      series: [
        {
          name: t('dashboard.charts.platform_distribution.series_name', ),
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '50%'],
          data: data,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          color: colors
        }
      ]
    };
  };

  /* eslint-disable-next-line no-unused-vars */
  const getDeviceGroupDistributionOption = () => {
    const systemGroupsData = stats.deviceGroups.systemGroups.map(group => ({
      name: group.name || 'Unknown',
      value: group.device_count || 0,
      itemStyle: { color: '#1890ff' }
    }));
    
    const customGroupsData = stats.deviceGroups.customGroups.map(group => ({
      name: group.name || 'Unknown',
      value: group.device_count || 0,
      itemStyle: { color: '#52c41a' }
    }));
    
    const allGroupsData = [...systemGroupsData, ...customGroupsData];
    
    return {
      title: {
        text: t('dashboard.charts.device_group_distribution.title', ),
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'normal'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: function(params) {
          const seriesName = params.seriesName;
          const name = params.name;
          const value = params.value;
          const percent = params.percent;
          return `${seriesName} <br/>${name}: ${value} ${t('dashboard.charts.device_group_distribution.tooltip.unit', )} (${percent}%)`;
        }
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        bottom: 20,
        data: allGroupsData.map(item => item.name)
      },
      series: [
        {
          name: t('dashboard.charts.device_group_distribution.series_name', ),
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '50%'],
          data: allGroupsData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };
  };

  /* eslint-disable-next-line no-unused-vars */
  const getSecurityScoreColor = (score) => {
    if (score >= 90) return '#52c41a';
    if (score >= 70) return '#faad14';
    return '#ff4d4f';
  };

  const getRiskLevelConfig = (level) => {
    switch (level) {
      case 'high':
        return { color: '#ff4d4f', text: t('dashboard.risk.high', ), icon: <WarningOutlined /> };
      case 'medium':
        return { color: '#faad14', text: t('dashboard.risk.medium', ), icon: <ExclamationCircleOutlined /> };
      default:
        return { color: '#52c41a', text: t('dashboard.risk.low', ), icon: <CheckCircleOutlined /> };
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  /* eslint-disable-next-line no-unused-vars */
  const riskConfig = getRiskLevelConfig(stats.security.riskLevel);

  return (
    <div>
      {licenseUsage?.max > 0 && licenseUsage?.current >= licenseUsage?.max && (
        <Alert
          type="error"
          showIcon
          message={t('dashboard.license_exceeded_title', )}
          description={t('dashboard.license_exceeded_desc', )}
          style={{ marginBottom: '16px' }}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          {t('dashboard.page.title', )}
        </Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {displayServerVersion && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('dashboard.server_version', { version: displayServerVersion })}
            </Text>
          )}
          <Divider type="vertical" style={{ margin: '0 6px' }} />
          <Tooltip title={t('dashboard.license.tooltip', )}>
            <Text type={licenseUsage.max > 0 && licenseUsage.current > licenseUsage.max ? 'danger' : 'secondary'} style={{ fontSize: '12px' }}>
              {t('dashboard.license_usage', { used: licenseUsage.current, total: licenseUsage.max || t('dashboard.unlimited', ) })}
            </Text>
          </Tooltip>
          {lastUpdate && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <ClockCircleOutlined /> {t('dashboard.last_update', )} {lastUpdate.toLocaleTimeString()}
            </Text>
          )}
          <Badge 
            status={refreshing ? 'processing' : 'success'} 
            text={refreshing ? t('dashboard.page.refreshing', ) : t('dashboard.live_monitoring', )} 
          />
          <SyncOutlined 
            spin={refreshing} 
            onClick={handleRefresh} 
            style={{ cursor: 'pointer', color: '#1890ff' }}
          />
        </div>
      </div>

      {/* 核心指标卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('dashboard.cards.total_devices.title', )}
              value={stats.devices.total}
              prefix={<LaptopOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              {t('dashboard.cards.total_devices.online', )} <span style={{ color: '#52c41a' }}>{stats.devices.online}</span> | 
              {t('dashboard.cards.total_devices.offline', )} <span style={{ color: '#ff4d4f' }}>{stats.devices.offline}</span>
              {stats.devices.pending > 0 && (
                <> | {t('dashboard.cards.total_devices.pending', )} <span style={{ color: '#faad14' }}>{stats.devices.pending}</span></>
              )}
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('dashboard.cards.today_activity.title', )}
              value={stats.violations.today}
              prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              {t('dashboard.cards.today_activity.violations', )} <span style={{ color: '#ff4d4f' }}>{stats.violations.todayViolations}</span>
              {stats.violations.unhandled > 0 && (
                <> | {t('dashboard.cards.today_activity.unhandled', )} <span style={{ color: '#ff4d4f' }}>{stats.violations.unhandled}</span></>
              )}
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('dashboard.cards.active_policies.title', )}
              value={stats.policies.active}
              prefix={<FileProtectOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              {t('dashboard.cards.active_policies.total', )} {stats.policies.total} | {t('dashboard.cards.active_policies.inactive', )} {stats.policies.inactive}
            </div>
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('dashboard.cards.device_groups.title', )}
              value={stats.deviceGroups.total}
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              {t('dashboard.cards.device_groups.system', )} <span style={{ color: '#1890ff' }}>{stats.deviceGroups.system}</span> | 
              {t('dashboard.cards.device_groups.custom', )} <span style={{ color: '#52c41a' }}>{stats.deviceGroups.custom}</span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 图表和分析 */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title={<><BugOutlined /> {t('dashboard.section.activity_trend_analysis', )}</>}>
            <ReactECharts 
              option={getViolationTrendOption()} 
              style={{ height: '350px' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card title={<><TeamOutlined /> {t('dashboard.charts.platform_distribution.title', )} </>}>
            <ReactECharts 
              option={getPlatformDistributionOption()} 
              style={{ height: '350px' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 威胁情报和系统状态 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title={
            <>
              <WarningOutlined /> {t('dashboard.section.threat_intel.overview', )}
              <Tooltip title={
                <div>
                  <div><strong>{t('dashboard.section.threat_intel.data_source.title', )}</strong></div>
                  <div>{t('dashboard.section.threat_intel.data_source.platforms', )}</div>
                  <div>{t('dashboard.section.threat_intel.data_source.file_hash_analysis', )}</div>
                  <div>{t('dashboard.section.threat_intel.data_source.activity_threat_stats', )}</div>
                  <div style={{ marginTop: '8px' }}><strong>{t('dashboard.section.threat_intel.level.title', )}</strong></div>
                  <div>{t('dashboard.section.threat_intel.level.severe', )}</div>
                  <div>{t('dashboard.section.threat_intel.level.high', )}</div>
                  <div>{t('dashboard.section.threat_intel.level.medium', )}</div>
                  <div>{t('dashboard.section.threat_intel.level.low', )}</div>
                </div>
              }>
                <QuestionCircleOutlined style={{ marginLeft: '8px', color: '#999', fontSize: '14px' }} />
              </Tooltip>
            </>
          }>
            <Alert
              message={t('dashboard.section.threat_intelligence.title', )}
              description={t('dashboard.section.threat_intelligence.desc', )}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            <Row gutter={[16, 16]}>
              <Col xs={24} md={6}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title={t('dashboard.section.threat_severity.critical', )}
                    value={stats.violations.by_severity?.critical || 0}
                    valueStyle={{ color: '#ff4d4f' }}
                    prefix={<ExclamationCircleOutlined />}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{t('dashboard.section.threat_severity.based_on_severity', )}</div>
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title={t('dashboard.section.threat_severity.high', )}
                    value={stats.violations.by_severity?.high || 0}
                    valueStyle={{ color: '#fa541c' }}
                    prefix={<WarningOutlined />}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{t('dashboard.section.threat_severity.based_on_severity', )}</div>
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title={t('dashboard.section.threat_severity.medium', )}
                    value={stats.violations.by_severity?.medium || 0}
                    valueStyle={{ color: '#faad14' }}
                    prefix={<WarningOutlined />}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{t('dashboard.section.threat_severity.based_on_severity', )}</div>
                </Card>
              </Col>
              <Col xs={24} md={6}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title={t('dashboard.section.threat_severity.low', )}
                    value={stats.violations.by_severity?.low || 0}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<CheckCircleOutlined />}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{t('dashboard.section.threat_severity.based_on_severity', )}</div>
                </Card>
              </Col>
            </Row>
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>{t('dashboard.section.incident_processing_status.title', )}</Text>
              <Progress 
                percent={stats.violations.total > 0 ? 
                  Math.round(((stats.violations.total - stats.violations.unhandled) / stats.violations.total) * 100) : 100
                }
                size="small"
                strokeColor="#52c41a"
                format={percent => t('dashboard.section.incident_processing_status.processed_percent', { percent })}
              />
            </div>
          </Card>
        </Col>
        
        <Col xs={24} lg={8}>
          <Card title={<><SafetyOutlined /> {t('dashboard.section.system_status.title', )}</>} style={{ height: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Alert
                message={t('dashboard.section.system_status.core_services_ok', )}
                description={t('dashboard.section.system_status.core_services_desc', )}
                type="success"
                showIcon
              />
              <Alert
                message={t('dashboard.section.system_status.db_ok', )}
                description={t('dashboard.section.system_status.db_desc', )}
                type="success"
                showIcon
              />
              {stats.violations.unhandled > 0 && (
                <Alert
                  message={t('dashboard.section.system_status.threats_unhandled', { count: stats.violations.unhandled })}
                  description={t('dashboard.section.system_status.threats_unhandled_desc', )}
                  type="warning"
                  showIcon
                />
              )}
              {stats.security.riskLevel === 'high' && (
                <Alert
                  message={t('dashboard.section.system_status.high_risk', )}
                  description={t('dashboard.section.system_status.high_risk_desc', )}
                  type="error"
                  showIcon
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
